import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import Select from 'components/ui/Select';
import { Checkbox } from 'components/ui/Checkbox';
import { useSelector } from 'react-redux';
import { fetchCategories } from 'services/categoryService';
import { getBusinessServices } from 'services/businessService';
 import { createBooking } from 'services/bookingService';
import { toast } from 'react-toastify';
import { isValidEmail, isValidMobileNumber } from 'utils/Validators';
import VehicleList from 'utils/VehicleList';

const BookingModal = ({ isOpen, onClose, booking, onSave, mode = 'add' }) => {
  const selectedBusiness = useSelector((state) => state?.business.selectedBusiness);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    registrationNo: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleColour:'',
    vehicleDescription:'',
    alternativePickUp:false,
    collectorName:'',
    collectorSurname:'',
    collectorPhone:'',
    collectorEmail:'',
    paid: false,
    bookingDate: new Date()?.toISOString()?.split('T')?.[0],
    category: '',
    amount:0,
    status:'Pending', //Pending, Completed, Cancelled
    notes: '',
    services:[]
  });

  //const [vehicleOptions, setVehicleOptions] = useState([]);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serviceGroups, setServiceGroups] = useState([]);

  const vehicleOptions = VehicleList.map(v => ({ value: v, label: v }));
  const colourOptions = [
    { value: 'White', label: 'White' },
    { value: 'Black', label: 'Black' },
    { value: 'Silver', label: 'Silver' },
    { value: 'Gray', label: 'Gray' },
    { value: 'Blue', label: 'Blue' },
    { value: 'Red', label: 'Red' },
    { value: 'Green', label: 'Green' },
    { value: 'Yellow', label: 'Yellow' },
    { value: 'Brown', label: 'Brown' },
    { value: 'Gold', label: 'Gold' },
    { value: 'Orange', label: 'Orange' },
    { value: 'Purple', label: 'Purple' },
    { value: 'Beige', label: 'Beige' },
    { value: 'Maroon', label: 'Maroon' },
    { value: 'Pink', label: 'Pink' }
  ];

  const resetFormData = ()=>{
    setFormData({
      name: '',
      surname: '',
      email: '',
      phone: '',
      registrationNo: '',
      vehicleMake: '',
      vehicleModel: '',
      vehicleColour:'',
      vehicleDescription:'',
      alternativePickUp:false,
      collectorName:'',
      collectorSurname:'',
      collectorPhone:'',
      collectorEmail:'',
      paid: false,
      bookingDate: new Date()?.toISOString()?.split('T')?.[0],
      category: '',
      amount:0,
      status:'Pending', //Pending, Completed, Cancelled
      notes: '',
      services:[]
    })
  }

  const loadCategories = async () => {
    if(!selectedBusiness) return;

    const response = await fetchCategories(selectedBusiness?.businessId);
    if (response?.data && response?.success) {
      const structuredCategories = response?.data?.map(cat => ({ value: cat?.id, label: cat?.name }));
      setCategories(structuredCategories);
    }

  }

  const getServicesByCategory = async (categoryId) => {
    const services = await getBusinessServices(selectedBusiness?.businessId, categoryId);
    const data = [];

    if(services.data && services.data.length > 0){
      const mainServices = services.data.filter(s => s.addOn === false);
      if(mainServices && mainServices.length > 0){
        data.push(structureServices(mainServices, false));
      }
      const extraServices = services.data.filter(s => s.addOn === true);
      if(extraServices && extraServices.length > 0){
        data.push(structureServices(extraServices, true));
      }
    }

    setServiceGroups(data);

  }

  const structureServices = (data=[], addOn=false)=>{
    const response = {
      name: addOn ? 'Add-On (Extra) Services' : 'Main Services',
      services: []
    }

    response.services = data.map(s => ({
      id: s.id,
      label: s.name,
      description: s.description,
      currentAmount: s.currentAmount,
      originalAmount: s.originalAmount
    }));

    return response;
  }

  const handleServiceChange = (serviceId, checked, amount) => {
    setFormData(prev => ({
      ...prev,
      services: checked 
        ? [...prev?.services, serviceId]
        : prev?.services?.filter(s => s !== serviceId),
      amount: checked ? prev.amount + amount : prev.amount - amount
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) newErrors.name = 'Name is required';
    if (formData?.email?.trim() !== '') {
      //newErrors.email = 'Email is required';
      if (!isValidEmail(formData?.email)) newErrors.email = 'Invalid email format';
    }
    //else if (!isValidEmail(formData?.email)) newErrors.email = 'Invalid email format';
    if (!formData?.phone?.trim()) newErrors.phone = 'Phone number is required';
    else if (!isValidMobileNumber(formData?.phone)) newErrors.phone = 'Invalid phone number, please enter a valid 10 digit number';
    if (!formData?.bookingDate) newErrors.bookingDate = 'Booking date is required';

    //Validate Vehicle Details
    if (!formData?.registrationNo?.trim()) newErrors.registrationNo = 'Registration Number is required';
    if (!formData?.vehicleMake?.trim()) newErrors.vehicleMake = 'Vehicle Make is required';
    if (!formData?.vehicleModel?.trim()) newErrors.vehicleModel = 'Vehicle Model is required';
    if (!formData?.vehicleColour?.trim()) newErrors.vehicleColour = 'Vehicel colour is required';

    //Validate Category
    if (!formData?.category) newErrors.category = 'Service category is required';

    //Validate Collector Details if alternativePickUp is true 
    if(formData?.alternativePickUp){
      if (!formData?.collectorName?.trim()) newErrors.collectorName = 'Collector name is required';
      if (!formData?.collectorPhone?.trim()) newErrors.collectorPhone = 'Collector phone is required';
      if (!formData?.collectorSurname?.trim()) newErrors.collectorSurname = 'Collector surname is required';
      // Validate collector email only if it is provided
      if (formData?.collectorEmail?.trim() !== ''){
        if (!isValidEmail(formData?.collectorEmail)) newErrors.collectorEmail = 'Invalid email format';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
   if (!validateForm()) return;

    setIsSubmitting(true);
    
    const payload = {
      bookingDate: formData?.bookingDate,
      hasSecondaryCollector: formData?.alternativePickUp,
      customerDetails:{
        name: formData?.name,
        surname: formData?.surname,
        emailAddress: formData?.email,
        contactNo: formData?.phone
      },
      secondaryCollectorDetails:{
        name: formData?.collectorName,
        surname: formData?.collectorSurname,
        contactNo: formData?.collectorPhone,
        email: formData?.collectorEmail
      },
      categoryId: formData?.category,
      services: formData?.services.map(sId => ({
         serviceReferenceId: sId,
         serviceName: serviceGroups?.flatMap(g => g?.services)?.find(s => s.id === sId)?.label || '',
         amount: serviceGroups?.flatMap(g => g?.services)?.find(s => s.id === sId)?.currentAmount || 0,
         addOn: false
      })),
      notes:{
        notes: formData?.notes || ''
      },
      vehicleDetails:{
        registrationNo: formData?.registrationNo,
        name: formData?.vehicleMake,
        model: formData?.vehicleModel,
        colour: formData?.vehicleColour,
        description: formData?.vehicleDescription || ''
      }
    }

    // await onSave();
    // onClose();
    // return;
    if(mode === 'add'){
      await addNewBooking(payload);
    }
    else if(mode === 'edit'){
      await updateBooking(payload);
    }

    //Reset Form
    resetFormData();

    await onSave();
  };

  const updateBooking = async (payload)=>{
    //To be implemented
    console.log('Payload to submit:', payload);
  }

  const addNewBooking = async (payload)=>{

    const response = await createBooking(selectedBusiness?.businessId, payload);

    if(response.success){
      toast.success(response.data.message || 'Booking created successfully');
      setIsSubmitting(false);
      onClose();
    }
    else{
      toast.error(response.data.message || 'Error creating booking. Please try again.');
      setIsSubmitting(false);
    }
    
  }


  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    if(field === 'category'){
      getServicesByCategory(value);
      resetServiceSelection();
    }
  };

  const resetServiceSelection = () => {
    setFormData(prev => ({ ...prev, services: [], amount:0 }));
  }

  useEffect(() => {
    loadCategories();
  }, [selectedBusiness]);

  useEffect(() => {
    //setVehicleOptions(VehicleList.map(v => ({ value: v, label: v })));

    if (booking && mode === 'edit') {
      setFormData({
        ...booking,
        bookingDate: booking?.bookingDate ? new Date(booking.bookingDate)?.toISOString()?.split('T')?.[0] : '',
        permissions: booking?.permissions || []
      });
    }
    setErrors({});
  }, [booking, mode, isOpen]);

  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-1002 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose}></div>
        
        <div className="relative bg-card border border-border rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {mode === 'add' ? 'Add New booking' : 'Edit booking'}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {mode === 'add' ? 'Enter booking details and select services' : 'Update booking details'}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground flex items-center">
                  <Icon name="User" size={20} className="mr-2" />
                  Basic Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Name"
                    type="text"
                    value={formData?.name}
                    onChange={(e) => handleInputChange('name', e?.target?.value)}
                    error={errors?.name}
                    required
                    placeholder="Enter name"
                  />
                  <Input
                    label="Surname"
                    type="text"
                    value={formData?.surname}
                    onChange={(e) => handleInputChange('surname', e?.target?.value)}
                    error={errors?.name}
                    required
                    placeholder="Enter surname"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                    label="Phone Number"
                    type="tel"
                    value={formData?.phone}
                    onChange={(e) => handleInputChange('phone', e?.target?.value)}
                    error={errors?.phone}
                    required
                    placeholder="Enter phone number"
                  /> 

                  <Input
                    label="Email Address (optional)"
                    type="email"
                    value={formData?.email}
                    onChange={(e) => handleInputChange('email', e?.target?.value)}
                    error={errors?.email}                    
                    placeholder="Enter email address"
                  />
                                   
                  <Input
                    label="Booking Date"
                    type="date"
                    value={formData?.bookingDate}
                    onChange={(e) => handleInputChange('bookingDate', e?.target?.value)}
                    error={errors?.bookingDate}
                    required
                  />
                </div>
              </div>

              {/* Pick Up Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground flex items-center">
                  <Icon name="Contact" size={20} className="mr-2" />
                  Picking Up Details
                </h3>

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData?.alternativePickUp}
                    onChange={(e) => handleInputChange('alternativePickUp', e?.target?.checked)}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                  />
                  <label htmlFor="active" className="text-sm font-medium text-foreground">
                    Is someone else picking up the vehicle?
                  </label>
                </div>
                
                {formData.alternativePickUp && <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Collector Name"
                    type="text"
                    value={formData?.collectorName}
                    onChange={(e) => handleInputChange('collectorName', e?.target?.value)}
                    placeholder="Enter collector's  name"
                    error={formData?.alternativePickUp && errors?.collectorName}
                    required
                  />

                  <Input
                    label="Collector Surname"
                    type="text"
                    value={formData?.collectorSurname}
                    onChange={(e) => handleInputChange('collectorSurname', e?.target?.value)}
                    placeholder="Enter collector's surname"
                    error={formData?.alternativePickUp && errors?.collectorSurname}
                    required
                  />
                  
                  <Input
                    label="Collector Contact Phone"
                    type="tel"
                    value={formData?.collectorPhone}
                    onChange={(e) => handleInputChange('collectorPhone', e?.target?.value)}
                    placeholder="Enter collector's contact phone"
                    error={formData?.alternativePickUp && errors?.collectorPhone}
                    required
                  />
                </div>}
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground flex items-center">
                  <Icon name="CarFront" size={20} className="mr-2" />
                  Vehicle Details
                </h3> 

                <div className='flex flex-col'>
                  <Select
                    label="Colour"
                    options={colourOptions}
                    value={formData?.vehicleColour}
                    onChange={(value) => handleInputChange('vehicleColour', value)}
                    error={errors?.vehicleColour}
                    showRequiredIndicator={true}
                    placeholder="Select Colour"
                  />

                  <Select
                    label="Vehicle Make"
                    options={vehicleOptions}
                    value={formData?.vehicleMake}
                    onChange={(value) => handleInputChange('vehicleMake', value)}
                    error={errors?.vehicleMake}
                    showRequiredIndicator={true}
                    placeholder="Select Vehicle Make"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* <Input
                    label="Vehicle Name"
                    type="text"
                    value={formData?.vehicleMake}
                    onChange={(e) => handleInputChange('vehicleMake', e?.target?.value)}
                    placeholder="Enter vehicle name"
                    error={errors?.vehicleMake}
                    required
                  /> */}
                  
                  <Input
                    label="Vehicle Model"
                    type="text"
                    value={formData?.vehicleModel}
                    onChange={(e) => handleInputChange('vehicleModel', e?.target?.value)}
                    placeholder="Enter vehicle model"
                    error={errors?.vehicleModel}
                    required
                  />

                  <Input
                    label="Vehicle Registration No"
                    type="text"
                    value={formData?.registrationNo}
                    onChange={(e) => handleInputChange('registrationNo', e?.target?.value)}
                    placeholder="Enter vehicle registration number"
                    error={errors?.registrationNo}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Vehicle Description
                  </label>
                  <textarea
                    value={formData?.vehicleDescription}
                    onChange={(e) => handleInputChange('vehicleDescription', e?.target?.value)}
                    placeholder="e.g damages, scratches, etc"
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                
              </div>
              
              {/* Category */}
              {/* <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground flex items-center">
                  <Icon name="ShoppingCart" size={20} className="mr-2" />
                  Service Category
                </h3>
                <div>
                  <Select
                    label="Category"
                    options={colourOptions}
                    value={formData?.category}
                    onChange={(value) => handleInputChange('category', value)}
                    error={errors?.vehicleColour}
                    required
                    placeholder="Select Category"
                  />
                </div>
              </div> */}

              {/* Services */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground flex items-center">
                  <Icon name="ShoppingCart" size={20} className="mr-2" />
                  Services
                </h3>

                <div>
                  <Select
                    label="Service Category"
                    options={categories}
                    value={formData?.category}
                    onChange={(value) => handleInputChange('category', value)}
                    error={errors?.category}
                    //required
                    placeholder="Select Category"
                    showRequiredIndicator={true}
                  />
                </div>
                {/*Only show services once category is select*/}
                {formData?.category &&
                <div className="space-y-6">
                  {serviceGroups?.map((serviceGroup) => (
                    <div key={serviceGroup?.name} className="space-y-3">
                      <h4 className="font-medium text-foreground">{serviceGroup?.name}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {serviceGroup?.services?.map((service) => (
                          <Checkbox
                            key={service?.id}
                            label={service?.label+' (R'+service?.currentAmount+')'}
                            description={service?.description}
                            checked={formData?.services?.includes(service?.id)}
                            onChange={(e) => handleServiceChange(service?.id, e?.target?.checked, service?.currentAmount)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                }

              </div>

              {/* Notes */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground flex items-center">
                  <Icon name="NotebookPen" size={20} className="mr-2" />
                  Notes
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Notes
                  </label>
                  <textarea
                    value={formData?.notes}
                    onChange={(e) => handleInputChange('notes', e?.target?.value)}
                    placeholder="Additional notes about the booking member..."
                    rows={3}
                    className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-6 border-t border-border bg-muted/30">
              <div>
                <span className="text-muted-foreground">
                  Total Amount: R{formData?.amount || '0.00'}
                </span>
              </div>
              <div className='flex items-center md:justify-end justify-between w-full md:w-auto gap-2'>
                  <Button 
                    variant="danger" onClick={onClose} disabled={isSubmitting}
                    iconName="X"
                    iconPosition="left"
                    iconSize={16}
                    className="w-full"
                  >
                    Cancel 
                  </Button>
                  <Button 
                    type="submit" 
                    loading={isSubmitting}
                    iconName="Save"
                    iconPosition="left"
                    iconSize={16}
                    className="w-full"
                  >
                    {mode === 'add' ? 'Add Booking' : 'Save Changes'}
                  </Button>
              </div>
              
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BookingModal;